import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, Car, FileUp, Loader2, MessageCircle, Send, QrCode } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { API } from '@/utils/api';
import { VehicleFields, EMPTY_VEHICLE } from './VehicleFields';
import { QrScan } from './QrScan';

const WA = {
  sent: ['Message envoyé', 'bg-emerald-100 text-emerald-700'], manual: ['À envoyer manuellement', 'bg-amber-100 text-amber-700'],
  failed: ['Échec de l\'envoi', 'bg-red-100 text-red-700'], not_sent: ['Non envoyé', 'bg-slate-100 text-slate-600'],
};

export const AttachVehicle = ({ appt, onChanged, mobile = false }) => {
  const [v, setV] = useState(null);
  const [busy, setBusy] = useState(false);
  const [qr, setQr] = useState(false);
  const ref = useRef(null);
  const scan = async (e) => {
    const f = e.target.files?.[0]; e.target.value = '';
    if (!f) return;
    setBusy(true);
    const fd = new FormData(); fd.append('file', f);
    try { const r = await axios.post(`${API}/vehicles/scan-carte-grise`, fd); setV(Object.fromEntries(Object.keys(EMPTY_VEHICLE).map(k => [k, r.data[k] ?? EMPTY_VEHICLE[k]]))); }
    catch { toast.error('Lecture impossible'); } finally { setBusy(false); }
  };
  const save = async () => {
    try { await axios.post(`${API}/agenda/appointments/${appt.id}/attach-vehicle`, { new_vehicle: v }); toast.success('Véhicule ajouté au rendez-vous'); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };
  return (
    <div className="p-3 rounded-lg border border-dashed space-y-2" data-testid="attach-vehicle-box">
      <p className="text-sm font-semibold flex items-center gap-2"><Car className="w-4 h-4" />Véhicule à compléter à l'arrivée</p>
      <input ref={ref} type="file" accept="image/*,application/pdf" {...(mobile ? { capture: "environment" } : {})} className="hidden" onChange={scan} data-testid="attach-vehicle-scan-input" />
      {!v ? (qr ? <div className="space-y-2"><QrScan onResult={(r) => setV(Object.fromEntries(Object.keys(EMPTY_VEHICLE).map(k => [k, r[k] ?? EMPTY_VEHICLE[k]])))} />
        <button type="button" className="text-xs underline text-slate-500" onClick={() => setQr(false)} data-testid="attach-vehicle-qr-back">Autres options</button></div> : (
        <div className="flex flex-wrap gap-2">
          {!mobile && <Button size="sm" className="bg-[#0E1116] text-[#FACC15] hover:bg-black" onClick={() => setQr(true)} data-testid="attach-vehicle-qr-btn"><QrCode className="w-4 h-4 mr-1" />Scanner avec le téléphone</Button>}
          <Button size="sm" variant="outline" onClick={() => ref.current.click()} disabled={busy} data-testid="attach-vehicle-scan-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FileUp className="w-4 h-4 mr-1" />Photo ou PDF</>}</Button>
          <Button size="sm" variant="outline" onClick={() => setV({ ...EMPTY_VEHICLE })} data-testid="attach-vehicle-manual-btn">Saisie manuelle</Button>
        </div>
      )) : (<><VehicleFields value={v} onChange={setV} compact /><Button size="sm" className="w-full" onClick={save} data-testid="attach-vehicle-save-btn">Ajouter le véhicule</Button></>)}
    </div>
  );
};

export const AppointmentStatusBox = ({ appt, onChanged, onCheckIn, onCheckOut }) => {
  const [confirmed, setConfirmed] = useState(!!appt.client_confirmed);
  const [wa, setWa] = useState(appt.whatsapp_status || 'not_sent');
  const toggle = async (v) => {
    setConfirmed(v);
    try { await axios.put(`${API}/agenda/appointments/${appt.id}/client-confirmed`, { confirmed: v }); onChanged(false); }
    catch { setConfirmed(!v); toast.error('Erreur'); }
  };
  const send = async () => {
    try {
      const r = await axios.post(`${API}/agenda/appointments/${appt.id}/send-confirmation`);
      setWa(r.data.status);
      if (r.data.status === 'sent') toast.success('Message WhatsApp envoyé');
      else if (r.data.status === 'manual' && r.data.whatsapp_url) window.open(r.data.whatsapp_url, '_blank');
      else toast.error(r.data.error || 'Échec de l\'envoi');
      onChanged(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };
  const [label, cls] = WA[wa] || WA.not_sent;
  return (
    <div className="space-y-2" data-testid="appointment-status-box">
      <div className="flex items-center gap-2 text-sm">
        <MessageCircle className="w-4 h-4 text-emerald-600" />WhatsApp :
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cls}`} data-testid="appointment-wa-status">{label}</span>
        {wa !== 'sent' && <Button size="sm" variant="outline" className="ml-auto h-7" onClick={send} data-testid="appointment-send-wa-btn"><Send className="w-3 h-3 mr-1" />Envoyer</Button>}
      </div>
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <Checkbox checked={confirmed} onCheckedChange={(v) => toggle(!!v)} data-testid="appointment-client-confirmed-checkbox" />
        <span className={confirmed ? 'font-semibold text-emerald-700 flex items-center gap-1' : ''}>{confirmed && <CheckCircle2 className="w-4 h-4" />}Confirmé par le client</span>
      </label>
      {appt.appt_kind === 'rdv' ? null : appt.checked_out
        ? <p className="text-sm font-semibold text-emerald-700 flex items-center gap-1" data-testid="appointment-checked-out">
            <CheckCircle2 className="w-4 h-4" />Véhicule restitué à {(appt.checked_out_at || '').slice(11, 16)}</p>
        : appt.checked_in
        ? <><p className="text-sm font-semibold text-emerald-700 flex items-center gap-1" data-testid="appointment-checked-in">
            <CheckCircle2 className="w-4 h-4" />Véhicule réceptionné à {(appt.checked_in_at || '').slice(11, 16)}</p>
          <Button className="w-full bg-emerald-700 text-white hover:bg-emerald-800" onClick={onCheckOut} data-testid="appointment-checkout-btn">Restituer le véhicule au client</Button></>
        : <Button className="w-full bg-[#0E1116] text-[#FACC15] hover:bg-black" onClick={onCheckIn} data-testid="appointment-checkin-btn">Réceptionner le véhicule</Button>}
      {appt.appt_kind !== 'rdv' && !appt.vehicle_id && !appt.checked_in && <AttachVehicle appt={appt} onChanged={() => onChanged(true)} />}
    </div>
  );
};
